import React, { useState } from "react";
import { Box, Grid, Paper, Skeleton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { WifiTetheringError } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import gitlabSelfManaged from "src/assets/icons/gitlab-self-hosted";
import { useAuth } from "src/components/providers/auth-context";
import notion from "../../assets/icons/notion";
import slack from "../../assets/icons/slack";
import gitlab from "../../assets/icons/gitlab";
import jira from "../../assets/icons/jira";
import googleWorkspace from "../../assets/icons/google-workspace";
import github from "../../assets/icons/github";
import IntegrationButton from "./integration-button";
import Terms from "../../components/terms";
import fetchStatus from "../../utils/api/fetch-status";
import microsoft from "../../assets/icons/microsoft";
import azure from "../../assets/icons/azure";

const svgs = {
    slack,
    jira,
    gitlab,
    google: googleWorkspace,
    github,
    gitlabSelfManaged,
    microsoft,
    azure,
    notion,
};

const IndividualSection = ({ userIntegrations }) => {
    const [futureIntegrations] = useState([]);
    const [activeIntegrations] = useState([
        "gitlab",
        "slack",
        "github",
        "gitlabSelfManaged",
        "google",
        "jira",
        "microsoft",
        "azure",
        "notion",
    ]);
    const [termsError, setTermsError] = useState(false);
    const [selectedIntegrationInd, setSelectedIntegrationInd] = useState();
    const [checkedTerms, setCheckedTerms] = useState(false);
    const [loading, setLoading] = useState(false);
    const { isDemo, isUserPayingCustomer } = useAuth();
    const navigate = useNavigate();

    React.useEffect(() => {
        if (
            userIntegrations.status === fetchStatus.PENDING ||
            userIntegrations.status === fetchStatus.REFETCHING
        ) {
            setLoading(true);
        } else {
            setLoading(false);
        }
    }, [userIntegrations.status]);

    const handleTermsChange = (event) => {
        setCheckedTerms(event.target.checked);
        setTermsError(false);
    };

    const handleIntegration = (clickedIntegration) => () => {
        if (termsError) setTermsError(false);
        const { integrations } = userIntegrations.data;
        const selected = integrations.find(
            (integration) => integration.name === clickedIntegration
        );
        setSelectedIntegrationInd(selected);
    };

    React.useEffect(() => {
        if (userIntegrations.data && selectedIntegrationInd) {
            handleIntegration(selectedIntegrationInd.name)();
        }
    }, [userIntegrations.data]);

    const handleConnect = async () => {
        if (!selectedIntegrationInd.connected && !selectedIntegrationInd.centrallyManaged) {
            if (!checkedTerms) return setTermsError(true);
            if (selectedIntegrationInd.name === "gitlab") {
                navigate("../integrations/permissions/gitlab");
            }
            if (selectedIntegrationInd.name === "gitlabSelfManaged") {
                navigate("../integrations/permissions/gitlab-self-hosted");
            }
            if (selectedIntegrationInd.name === "slack")
                navigate("../integrations/permissions/slack");
            if (selectedIntegrationInd.name === "github")
                navigate("../integrations/permissions/github");
            if (selectedIntegrationInd.name === "google")
                navigate("../integrations/permissions/google");
            if (selectedIntegrationInd.name === "jira")
                navigate("../integrations/permissions/jira");
            if (selectedIntegrationInd.name === "notion")
                navigate("../integrations/permissions/notion");
            if (selectedIntegrationInd.name === "microsoft")
                navigate("../integrations/permissions/microsoft");
            if (selectedIntegrationInd.name === "azure")
                navigate("../integrations/permissions/azure");
        }
        if (
            selectedIntegrationInd.connected ||
            (!selectedIntegrationInd.connected && selectedIntegrationInd.centrallyManaged)
        ) {
            if (selectedIntegrationInd.name === "gitlab")
                navigate("../integrations/settings/gitlab", {
                    replace: true,
                });
            if (selectedIntegrationInd.name === "gitlabSelfManaged")
                navigate("../integrations/settings/gitlab-self-hosted", {
                    replace: true,
                });
            if (selectedIntegrationInd.name === "slack")
                navigate("../integrations/settings/slack", {
                    replace: true,
                });
            if (selectedIntegrationInd.name === "github")
                navigate("../integrations/settings/github", {
                    replace: true,
                });
            if (selectedIntegrationInd.name === "google")
                navigate("../integrations/settings/google", {
                    replace: true,
                });
            if (selectedIntegrationInd.name === "jira")
                navigate("../integrations/settings/jira", {
                    replace: true,
                });
            if (selectedIntegrationInd.name === "notion")
                navigate("../integrations/settings/notion", {
                    replace: true,
                });
            if (selectedIntegrationInd.name === "microsoft")
                navigate("../integrations/settings/microsoft", {
                    replace: true,
                });
            if (selectedIntegrationInd.name === "azure")
                navigate("../integrations/settings/azure", {
                    replace: true,
                });
        }
        return undefined;
    };

    const renderSkeletons = () => {
        return [0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <Grid item key={i}>
                <Skeleton>
                    <IntegrationButton />
                </Skeleton>
            </Grid>
        ));
    };

    const renderError = () => {
        return [0].map((i) => (
            <Grid container item key={i} alignItems="center" direction="column">
                <Grid item>
                    <WifiTetheringError fontSize="large" />
                </Grid>
                <Grid item>Error retrieving data</Grid>
            </Grid>
        ));
    };

    const isDisabled = ({ name, integrationType, centrallyManaged }) => {
        if (name === "github") return integrationType === "app" || centrallyManaged;
        if (name === "jira") return centrallyManaged;
        if (name === "notion") return centrallyManaged;
        if (name === "gitlabSelfManaged") return centrallyManaged;
        if (name === "microsoft") return centrallyManaged;
        if (name === "azure") return centrallyManaged;
        return false;
    };

    const renderIndividualIntegrations = () => {
        if (userIntegrations.status === fetchStatus.PENDING) return renderSkeletons();
        if (userIntegrations.status === fetchStatus.ERROR) return renderError();
        const { integrations } = userIntegrations.data;
        const current = integrations
            .filter(({ name }) => activeIntegrations.includes(name))
            .map(
                ({
                    name,
                    connected,
                    centrallyManaged,
                    invalid,
                    invalidationReason,
                    integrationType,
                }) => (
                    <Grid item>
                        <IntegrationButton
                            name={name}
                            svg={svgs[name]}
                            checked={selectedIntegrationInd && selectedIntegrationInd.name === name}
                            connected={connected}
                            centrallyManaged={centrallyManaged}
                            invalid={invalid}
                            invalidationReason={invalidationReason}
                            action={handleIntegration(name)}
                            disabled={isDisabled({
                                name,
                                connected,
                                integrationType,
                                centrallyManaged,
                            })}
                        />
                    </Grid>
                )
            );

        const futureFilteredIntegrations = futureIntegrations.filter(
            (futureIntegration) => !activeIntegrations.includes(futureIntegration.name)
        );

        const future = futureFilteredIntegrations.map(
            ({ name, connected, invalid, invalidationReason }) => (
                <Grid item>
                    <IntegrationButton
                        svg={svgs[name]}
                        checked={selectedIntegrationInd && selectedIntegrationInd.name === name}
                        connected={connected}
                        invalid={invalid}
                        invalidationReason={invalidationReason}
                        action={handleIntegration(name)}
                        future
                    />
                </Grid>
            )
        );
        return (
            <>
                {current}
                {future}
            </>
        );
    };

    const isConnected =
        (selectedIntegrationInd &&
            selectedIntegrationInd.connected &&
            !selectedIntegrationInd.centrallyManaged) ||
        (selectedIntegrationInd &&
            selectedIntegrationInd.connected &&
            selectedIntegrationInd.centrallyManaged) ||
        (selectedIntegrationInd &&
            !selectedIntegrationInd.connected &&
            selectedIntegrationInd.centrallyManaged);

    return (
        <Paper
            style={{
                width: "100%",
                maxWidth: "794px",
                borderRadius: "14px",
            }}
            className="tour-integrations"
        >
            <Box my={3} mx={4} pb={1}>
                <Grid container spacing={5} direction="column">
                    <Grid item>
                        <h3 style={{ marginTop: 0 }}>Individual integrations</h3>
                    </Grid>
                    <Grid
                        container
                        item
                        spacing={2}
                        justifyContent="center"
                        pt={0}
                        style={{ paddingTop: 0 }}
                    >
                        {renderIndividualIntegrations()}
                    </Grid>
                    {selectedIntegrationInd &&
                        !selectedIntegrationInd.connected &&
                        !selectedIntegrationInd.centrallyManaged && (
                            <Grid item alignItems="center" style={{ display: "flex" }}>
                                <Terms
                                    checkedTerms={checkedTerms}
                                    handleTermsChange={handleTermsChange}
                                    termsError={termsError}
                                />
                            </Grid>
                        )}
                    {selectedIntegrationInd && !isDemo && isUserPayingCustomer && (
                        <Grid item>
                            <LoadingButton
                                variant="contained"
                                color={isConnected ? "secondary" : "primary"}
                                onClick={handleConnect}
                                loading={loading}
                                disabled={
                                    !selectedIntegrationInd ||
                                    (!isConnected && !isUserPayingCustomer)
                                }
                            >
                                {isConnected ? "Settings" : "Connect"}
                            </LoadingButton>
                        </Grid>
                    )}
                </Grid>
            </Box>
        </Paper>
    );
};

export default IndividualSection;
